import { useEffect, useRef } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import api from '@/services/api';

// ── Types ────────────────────────────────────────────────────────────────────

export type IngestionStatus =
  | 'none'
  | 'queued'
  | 'running'
  | 'completed'
  | 'failed';

/** GET /territories/{insee}/ingest */
export interface IngestionState {
  code_insee: string;
  status: IngestionStatus;
  step: string | null;
  message: string | null;
  started_at: string | null;
  finished_at: string | null;
}

/** POST /territories/{insee}/ingest */
export interface StartIngestionResponse {
  code_insee: string;
  status: 'running';
  started_at: string;
}

/** Statuses for which the backend is still working and we keep polling. */
const ACTIVE_STATUSES: readonly IngestionStatus[] = ['queued', 'running'];

// T4.3 (finding H12) — garder en phase avec MAX_INGEST_POPULATION backend
// (backend/app/api/v1/territories.py). Duplication assumée : le 422 serveur
// reste la VRAIE barrière ; cette constante ne sert qu'à afficher la limite
// PROACTIVEMENT côté client (avant le clic), pour éviter un 422 surprise.
export const MAX_INGEST_POPULATION = 150_000;

/** True si la population dépasse la limite d'ingestion (population null/undefined
 * → pas de restriction, cf. backend : donnée manquante ≠ commune trop grande). */
export function exceedsIngestPopulationLimit(population: number | null | undefined): boolean {
  return population != null && population > MAX_INGEST_POPULATION;
}

export function isIngestionActive(status: IngestionStatus | undefined): boolean {
  return status != null && ACTIVE_STATUSES.includes(status);
}

// ── Status polling ───────────────────────────────────────────────────────────

/**
 * Polls the ingestion status for a commune.
 * Refetches every 4 s while the job is queued/running, then stops.
 */
export function useIngestionStatus(codeInsee: string | null) {
  return useQuery<IngestionState>({
    queryKey: ['ingestion', codeInsee],
    queryFn: async () => {
      const { data } = await api.get<IngestionState>(
        `/territories/${codeInsee}/ingest`,
      );
      return data;
    },
    enabled: !!codeInsee,
    // Poll while active, otherwise stop (false).
    refetchInterval: (query) =>
      isIngestionActive(query.state.data?.status) ? 4000 : false,
  });
}

// ── Start / refresh ingestion ────────────────────────────────────────────────

/**
 * Triggers a full background ingestion for a commune.
 * On success it kicks the status query so polling starts immediately.
 */
export function useStartIngestion() {
  const qc = useQueryClient();
  return useMutation<StartIngestionResponse, unknown, string>({
    mutationFn: async (codeInsee: string) => {
      const { data } = await api.post<StartIngestionResponse>(
        `/territories/${codeInsee}/ingest`,
      );
      return data;
    },
    onSuccess: (_data, codeInsee) => {
      qc.invalidateQueries({ queryKey: ['ingestion', codeInsee] });
    },
  });
}

// ── Combined convenience hook ────────────────────────────────────────────────

/**
 * One-stop hook for the load/refresh flow of a commune's heavy data.
 *
 * - Exposes the live ingestion status (with polling).
 * - Exposes `start()` to launch (or relaunch) the ingestion.
 * - When the status transitions to `completed`, invalidates the territories
 *   list and every query scoped to this commune so the whole app refreshes
 *   with the freshly ingested data.
 *
 * Gate (has_full_data === false) and the sidebar "Rafraîchir" button are
 * mutually exclusive consumers of the same commune, so the completion effect
 * never runs twice for one job.
 */
export function useIngestion(codeInsee: string | null) {
  const qc = useQueryClient();
  const statusQuery = useIngestionStatus(codeInsee);
  const start = useStartIngestion();

  const status = statusQuery.data?.status;
  const step = statusQuery.data?.step ?? null;
  const prevStatus = useRef<IngestionStatus | undefined>(undefined);
  const prevStep = useRef<string | null>(null);

  /** Invalidates every territory-scoped query (indicators, stats, renovation…). */
  const invalidateTerritoryScoped = (insee: string) => {
    qc.invalidateQueries({ queryKey: ['territories'] });
    qc.invalidateQueries({ queryKey: ['territory', insee] });
    qc.invalidateQueries({
      predicate: (query) =>
        Array.isArray(query.queryKey) &&
        query.queryKey.includes(insee) &&
        query.queryKey[0] !== 'ingestion',
    });
  };

  useEffect(() => {
    if (!codeInsee) return;
    const completed = prevStatus.current !== 'completed' && status === 'completed';
    // Rafraîchissement PROGRESSIF : le pipeline avance par étapes
    // (bâtiments → végétation → température). Chaque changement d'étape signale
    // que les données de l'étape précédente viennent d'atterrir en base ; on
    // rafraîchit donc dès ce moment plutôt que d'attendre la fin du pipeline
    // (≈ 8-10 min). La complétion déclenche une dernière invalidation + le flip
    // has_full_data de la liste.
    const stepAdvanced =
      isIngestionActive(status) && step != null && step !== prevStep.current
      && prevStep.current != null;
    if (completed || stepAdvanced) {
      invalidateTerritoryScoped(codeInsee);
    }
    prevStatus.current = status;
    prevStep.current = step;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [status, step, codeInsee, qc]);

  return {
    status,
    state: statusQuery.data,
    step: statusQuery.data?.step ?? null,
    message: statusQuery.data?.message ?? null,
    isActive: isIngestionActive(status),
    start: (insee: string) => start.mutate(insee),
    isStarting: start.isPending,
    startError: start.isError,
  };
}
